import { useCancelPatientAppointment } from '@/api/hooks/useCancelPatientAppointment'
import type { UpcomingVisit } from '@/api/types/ProviderApi'
import { SheetStack, type StackView } from '@/components/Rows/SheetStack'
import { analytics } from '@/lib/analytics'
import { Button, ButtonText } from '@/ui/core/button'
import { SheetTitle } from '@/ui/core/sheet'
import { VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { router } from 'expo-router'
import { useState } from 'react'
import { View } from 'react-native'
import Toast from 'react-native-toast-message'

/** A link on its own line, centred against the link variant's `self-start`. */
const QuietLink = ({ label, onPress }: { label: string; onPress: () => void }) => (
	<View className="items-center">
		<Button variant="link" style={{ alignSelf: 'center' }} onPress={onPress}>
			<ButtonText>{label}</ButtonText>
		</Button>
	</View>
)

/**
 * The ways out of a visit, behind the question a member asks when they want one.
 *
 * It lives in the slot's small print — in the line itself, after the sentence that says why the
 * button above is where it is, rather than on a line of its own beneath it. Four placements in: a
 * button beside the action read as its equal, a blush card of its own read as a second subject,
 * a link floating under the card read as neither, and a line of its own inside the slot read as
 * a second caption. Set in the sentence, it is what it is: an aside, at the weight of an aside,
 * where a member is already reading about when they can do things.
 *
 * The sheet steps rather than stacking a dialog on itself: Move It is the likelier answer and
 * leads, Cancel is a link under it, and pressing that carries this same sheet across to the
 * question that has to be asked before a time goes back to the studio. A modal over a modal is
 * two windows on iOS and the second one lands behind the first's scrim.
 *
 * The step is Billing's, not a second one written here. `SheetStack` slides the two panels as a
 * pair and eases the sheet's height from one to the other, so a member who has stepped from a
 * payment plan into a payment meets the same motion stepping from here into the question — and
 * the disc becomes the way back in both.
 */
export const CantMakeIt = ({
	visit,
	helper,
}: { visit: UpcomingVisit; helper?: string }) => {
	const [view, setView] = useState<StackView<UpcomingVisit> | null>(null)
	const { mutate: cancelAppointment, isPending } = useCancelPatientAppointment()

	const close = () => setView(null)
	const ask = () => setView({ kind: 'item', item: visit, fromList: true })
	const back = () => setView({ kind: 'list' })

	const onMove = () => {
		close()
		// Straight into the picker: the old visit is only released once a new time is booked, so
		// there is nothing left to warn about.
		router.push({
			pathname: '/schedule',
			params: { mode: 'reschedule', appointmentId: String(visit.appointmentId) },
		})
	}

	const onCancel = () =>
		cancelAppointment(
			{ appointmentId: visit.appointmentId },
			{
				onSuccess: () => {
					analytics.trackEvent('cancelled-appointment', {
						appointmentId: visit.appointmentId,
					})
					close()
					Toast.show({ type: 'success', text1: 'Your visit has been cancelled' })
					// Nothing left on this page to look at: the visit it is about is gone.
					if (router.canGoBack()) {
						router.back()
					} else {
						router.replace('/')
					}
				},
				onError: () =>
					Toast.show({
						type: 'error',
						text1: 'We could not cancel your visit, please try again',
					}),
			}
		)

	return (
		<>
			{/* One line, two voices: the sentence in muted, the question in ink and underlined,
			    which is the whole difference between a fact and a control at twelve point. Nested
			    rather than laid beside it, so a long sentence wraps and carries the question with
			    it instead of leaving it stranded.

			    Centred under a full-width button whose own label is centred — the same place the
			    booked screen puts its note. Ranged left it hung off one end of a thing the width
			    of the card.
			
			    In ink, not muted. It is the only sentence in the card that says why the button
			    above it cannot be pressed yet, and set grey under a grey label it read as the
			    button's disabled state carrying on rather than as the answer to it. */}
			<Text size="body2" className="text-center">
				{helper ? `${helper} ` : ''}
				{/* Medium, not bold: enough to carry the weight of a control against the
				    sentence it sits in, where Bold at twelve point beside Book reads as a shout.
				    The face the reschedule note already uses for the same job.

				    Title case, as every control in this app is — "Move It", "Add to Calendar",
				    "Pick a Time". Set inside running copy it is the one thing marking these four
				    words as a thing to press rather than the end of the sentence, and being
				    twelve point and inline it can use the help. */}
				<Text
					size="body2"
					underlined
					className="font-body-medium"
					onPress={() => {
						analytics.trackClick('visit-cant-make-it', {
							appointmentId: visit.appointmentId,
						})
						back()
					}}
				>
					Can't Make It?
				</Text>
			</Text>

			<SheetStack
				view={view}
				onClose={close}
				list={
					<View className="pt-2">
						{/* No line under it. A member who opened this from the card is holding the
						    date and the time on the screen behind the sheet, and the step that
						    matters — the one that releases the time — says it there, where it is
						    the thing being given up rather than a restatement. */}
						<SheetTitle glyph="calendar">Can't Make It?</SheetTitle>
						<VStack gap={16} className="px-4 pb-8">
							<Button size="lg" fullWidth onPress={onMove}>
								<ButtonText>Move It</ButtonText>
							</Button>
							<QuietLink label="Cancel Visit" onPress={ask} />
						</VStack>
					</View>
				}
				item={(asked) => (
					<View className="pt-2">
						{/* The disc becomes the way back, which is what `SheetTitle` does with a
						    sheet that has stepped into a detail. */}
						<SheetTitle glyph="x" onBack={back}>
							Cancel This Visit?
						</SheetTitle>
						<VStack gap={14} className="px-4 pb-8">
							<Button size="lg" fullWidth isLoading={isPending} onPress={onCancel}>
								<ButtonText>Cancel Visit</ButtonText>
							</Button>
							{/* Under the button, not over it: what a member wants from this step is
							    the thing they came to press, and what releasing the time means is
							    a footnote to it rather than a paragraph in front of it.

							    There is no "Keep It" any more either. The sheet has three ways out
							    already — the disc, the dimmed page, a swipe down — and a second
							    button under the first was the app talking a member out of the
							    thing it had just offered them. */}
							<Text size="body2" className="text-center">
								Your time on {asked.startTimeFormatted} will be released. You can book
								again whenever you like.
							</Text>
						</VStack>
					</View>
				)}
			/>
		</>
	)
}
